/*global SV_ENV:false */

/**
 * @module Messages
 */

/**
 * Sent by the player before leaving the page.
 * (window.beforeunload event)
 *
 * @function before_unload
 * @param {String}  messageKind='before_unload'
 */

/**
 * Sent by the player when a click is performed which isn't handled internally.
 *
 * @function click
 * @param {String}  messageKind='click'
 */

/**
 * Sent by the player when the video being played ends.
 * Triggered by the implementation's ended event.
 *
 * @function ended
 * @param {String}  messageKind='ended'
 * @param {Object}  o
 * @param {String}  o.requestURL
 * @param {Boolean} o.isAd
 * @param {Boolean} o.isLive
 */

/**
 * Sent by the player when a video is loaded.
 *
 * @function load
 * @param {String}  messageKind='load'
 * @param {Object}  o
 * @param {String}  o.requestURL
 * @param {Boolean} o.isAd
 * @param {Boolean} o.isLive
 */

/**
 * Sent by the player when the progress event is triggered by the implementation.
 *
 * @function progress
 * @param {String}  messageKind='progress'
 * @param {Object}  info
 * @param {Number}  info.start
 * @param {Number}  info.end
 * @param {Number}  info.length
 */


/**
 * Sent by the player is resized (element/window resize, etc).
 *
 * @function resize
 * @param {String}  messageKind='resize'
 * @param {Number[2]} dims
 * @param {Number[2]} video
 * @param {Boolean}   isMaximized
 */

/**
 * Sent by the player when the video state changes (can be triggered automatically).
 *
 * @function state_changed
 * @param {String}  messageKind='state_changed'
 * @param {String} state either 'play' or 'pause'
 */

/**
 * Sent by the player when current video time changes
 *
 * @function time_updated
 * @param {String}  messageKind='time_updated'
 * @param {Number}  currentTime in seconds
 */

/**
 * Sent by the player when the video volume changes (can be triggered automatically).
 *
 * @function volume_changed
 * @param {String}  messageKind='volume_changed'
 * @param {Number}  value
 */

/**
 * Sent by the GUI when the user changes the video time explicitly.
 *
 * @function h_scrub
 * @param {String} messageKind='h_scrub'
- * @param {Number} value time in seconds
 */


/**
 * Each following namespace is implemented as a plugin, loaded by the VideoHighEnd instance.
 *
 * Plugins prefixed with `sv` have dependencies of the SAPO Vídeos infrastructure.
 *
 * @module Plugins
 */


/**
 * @module Controllers
 *
 *
 * This is the public interface to the Video player for high end devices (desktop computers, iPads and recent android devices).
 *
 * IPhones and older mobile devices with make use of the alternative VideoNative implementation.
 *
 * @class VideoHighEnd
 */

(function(window, undefined) {

    'use strict';

    var _          = SAPO.Project.Video._,
        Css        = SAPO.Dom.Css,
        Element    = SAPO.Dom.Element,
        Event      = SAPO.Dom.Event,
        Feat       = SAPO.Project.Video.Features,
        objectKeys = SAPO.Utility.objectKeys;

    var processVideoSourceRgx = /^(https?:)?(\/\/)?([a-z0-9\.]+)\/([^\/]{20})\/mov\/(\d+)/;


    if (SAPO.Browser.IE) {
        (function() {
            var ver = parseInt(SAPO.Browser.version, 10);
            var classes = [];
            //if (ver < 11) { classes.push('lt-ie11'); }
            if (ver < 10) { classes.push('lt-ie10'); }
            if (ver <  9) { classes.push('lt-ie9');  }
            document.documentElement.className = classes.join(' ');
        })();
    }


    var inIframe = window.parent.SAPO.Project.Video.Features.inExternalIframe();
    //inIframe = true;


    // closure-scoped variables
    var options, ui, va, pub, pubD, FS, linearExtras = {};

    var guidHistory = [];
    var requestURLHistory = [];

    var getPreviousGuid = function() {
        return guidHistory[ guidHistory.length - 2 ];
    };

    var getPreviousRequestURL = function() {
        return requestURLHistory[ requestURLHistory.length - 2 ];
    };


    var guidCount = {};

    var slt = String.fromCharCode(33, 60, 122, 120, 99, 118, 98, 110, 109, 44, 33); // '!<zxcvbnm,!'



    SAPO.namespace('Project.Video.Controllers');

    /*
     * this array will be filled by the plugins themselves.
     * on contruction, the controller will instantiate each one.
     */
    var plugins = [];
    SAPO.Project.Video.Controllers._plugins = plugins;



    // localStorage abstraction
    var load = SAPO.Utility.load;
    var save = SAPO.Utility.save;



    var encIdRgx = /mov\/(\d+)$/;

    var replaceEncId = function(url, newEncId) {
        return url.replace(encIdRgx, 'mov/'+newEncId);
    };



    var currentVideo = {
        autoplay:           undefined,
        requestURL:         undefined,
        metadata:           undefined,
        zoneId:             undefined,
        vast:               undefined,
        redirectURL:        undefined,
        isAd:               undefined,
        isLive:             undefined
    };

    var vastDocuments     = {}; // por GUID (sem /mov/1)
    var metadataDocuments = {}; // por GUID
    var previousTimes     = {}; // pro GUID
    var lastHumanState = 'play'; // Initialize this a 'play' for 'autoplay' cases
    var lastMachineState = 'play'; // Track last video state as set by state_changed. Helps fixing edge cases with IE not firing 'state_changed' when video ends


    var setPoster = function(md) {
        if (!md || !md.poster) {
            //console.log('NO DATA FOR POSTER!');
        }
        else if(options.noSapoVideos){
            va.updatePoster(md.poster);
        } else {
            var imgUrl = SAPO.Project.Video.thumbs.cropPosterTransform(
                md.poster + '/560x420',
                [560, 420],
                md.dimensions,
                md.duration
            );

            //console.log('POSTER URL: ' + imgUrl);

            va.updatePoster(imgUrl);
        }
    };

    // LOADING FSM
    var highEnd;

    var fsm = SAPO.Project.Video.FSM.create({
        initial: 'IDLE',
        debug: 0,
        error: function() {}, // allow events not in FSM to be no-ops.
        events: [ // regra é todas as transições que saem de cada nó, clockwise
            {from: 'IDLE',          to:'LOAD_REQSTED',  name:'load_msg'},
            {from: 'LOAD_REQSTED',  to:'IDLE',          name:'metadata_error'},
            {from: 'LOAD_REQSTED',  to:'HAS_METADATA',  name:'metadata_ok'},
            {from: 'HAS_METADATA',  to:'NEEDS_VAST',    name:'needs_vast'},
            {from: 'HAS_METADATA',  to:'WAITING_PLAY',  name:'waiting_play'},
            {from: 'NEEDS_VAST',    to:'LOAD_REQSTED',  name:'load_msg'},
            {from: 'NEEDS_VAST',    to:'WAITING_PLAY',  name:'vast_wo_preroll'},
            {from: 'NEEDS_VAST',    to:'NEEDS_PLAYBCK', name:'vast_wo_preroll_auto'},
            {from: 'WAITING_PLAY',  to:'NEEDS_VAST',    name:'needs_vast2'},
            {from: 'WAITING_PLAY',  to:'NEEDS_PLAYBCK', name:'needs_playbck'},
            {from: 'WAITING_PLAY',  to:'LOAD_REQSTED',  name:'load_msg'},
            {from: 'NEEDS_PLAYBCK', to:'IDLE',          name:'redirect_url_ok_error'}
        ],

        callbacks: {
            onstate: function(name, from, to/*, args*/) {
                if (!pub) { return; }
                pub('fsm_state_changed', {from:from, to:to, name:name});
            },

            onenterIDLE: function() {
                if (va) {
                    // hides poster
                    //console.log('CLEARING POSTER');
                    /*
                    if (!Feat.isIPhoneOrIPod) {
                        va.clearPoster();
                    }*/
                    /*    setPoster(currentVideo.metadata);
                    }*/

                    var redURL = currentVideo.redirectURL;
                    if (redURL) {
                        pub('load', {
                            requestURL:  currentVideo.requestURL,
                            isAd:        currentVideo.isAd,
                            isLive:      currentVideo.isLive,
                            guidURL:     currentVideo.guidURL,
                            redirectURL: redURL,
                            mimeType:    currentVideo.mimeType,
                            isExternal:  currentVideo.isExternal
                        });

                        if (options.autoplay) {
                            currentVideo.autoplay = true;
                            delete options.autoplay;
                        }

                        // sets video src for redirect or original and plays video
                        va._options.autoplay = currentVideo.autoplay;
                        pub('state_changed', currentVideo.autoplay ? 'play' : 'pause');

                        va._options.startTime = ( currentVideo.isAd ? undefined : (options.startTime || previousTimes[ currentVideo.guidURL ]) );
                        if (isFinite(va._options.startTime)) {
                            //console.log('START TIME %s', va._options.startTime.toFixed(1));
                            pub('time_updated', va._options.startTime);

                            if (va._options.startTime === options.startTime) {
                                delete options.startTime;
                            }

                            if (va._options.startTime === previousTimes[ currentVideo.guidURL ]) {
                                delete previousTimes[ currentVideo.guidURL ];
                            }

                        }

                        var o = {
                            src:    redURL,
                            // src:    'http://pub.videos.sapo.pt/1SdAk6L3b3zowmZZTHA3/mov/2',
                            type:   currentVideo.mimeType,
                            extras: linearExtras[redURL]
                        };

                        //console.log('V -> VA LOAD: ' + JSON.stringify(o, null, '  '));
                        va.load(o);
                    }
                }
            },

            onenterLOAD_REQSTED: function() {
                // console.log('onenterLOAD_REQSTED: ', currentVideo.requestURL);

                var guid = currentVideo.guidURL;
                var newEncId = currentVideo.requestURL.split('/').pop();
                var tmp = newEncId.indexOf('#');
                if (tmp !== -1) {
                    newEncId = newEncId.substring(0, tmp);
                }

                var onMetadata = function(err, md) {
                    if (err) {
                        try {
                            highEnd.unload();
                        } catch (ex) {}
                        // pub('error', 'NO_SV_METADATA');
                        va.fire('error', 'NO_SV_METADATA');
                        return fsm.metadata_error();
                    }

                    // fix inexistant HD movies
                    if (!md.hasHD && highEnd.isHD()) {
                        var newURL = [md.pageURI, 'mov', '1'].join('/');
                        //console.log('CORRECTING INEXISTENT REQUEST URL %s TO %s...', currentVideo.requestURL, newURL);
                        currentVideo.requestURL = newURL;
                        return fsm.onenterLOAD_REQSTED();
                    }

                    // enrich metadata with isLive and isAd
                    md.isLive = currentVideo.isLive;
                    md.isAd   = currentVideo.isAd;

                    currentVideo.metadata = md;
                    metadataDocuments[guid] = md;
                    //console.log('SAVING METADATA FOR ', guid);

                    pub('metadata_changed', md);
                    // console.log('METADATA:\n' + JSON.stringify(md, null, '  '));

                    fsm.metadata_ok();
                };


                var md = metadataDocuments[guid];
                if (md) {
                    md = JSON.parse( JSON.stringify(md) ); // clone

                    if (md.fromSapoVideos) {
                        md.encid = newEncId;
                        md.requestURL = replaceEncId(md.requestURL, newEncId);
                        md.fullURI    = replaceEncId(md.fullURI,    newEncId);
                    }

                    //console.log('REUSING METADATA FOR %s, SETTING REVISITED TRUE', guid);
                    md.revisited = true;

                    setTimeout(function() {
                        onMetadata(null, md);
                    }, 0);
                }
                else {
                    // request metadata...
                    //console.log('FETCHING NEW GUID ' + guid);

                    if(!options.noSapoVideos && currentVideo.requestURL !== currentVideo.guidURL) {
                        SAPO.Project.Video.sapoVideosMetadata(
                            currentVideo.requestURL,
                            function(err, md) {
                                onMetadata(err, md);
                            }
                        );
                    } else {
                        fsm.metadata_ok();
                    }
                }
            },

            onenterHAS_METADATA: function() {
                var md = currentVideo.metadata;

                var zoneId = 0;

                if (options.zoneId !== undefined) {
                    zoneId = options.zoneId;
                }
                else if (md.advertise && isFinite(md.adwordsId)) {
                    zoneId = md.adwordsId;
                }
                else if (md.advertise) {
                    zoneId = 642;
                }

                currentVideo.zoneId = zoneId;

                if ( // não quer ou não pode fazer autoplay
                    !currentVideo.autoplay ||
                    !va.canAutoplay()
                ) {
                    highEnd.unloading();
                    return fsm.waiting_play();
                }

                if ( // tem vast para pedir
                    zoneId === 0      ||
                    currentVideo.isAd ||
                    ( vastDocuments[currentVideo.guidURL] && currentVideo.wasAd )
                ) {
                    return fsm.waiting_play();
                }

                fsm.needs_vast();
            },

            onenterNEEDS_VAST: function() {
                // setup vast arguments
                var md = currentVideo.metadata;
                var zoneId = currentVideo.zoneId;
                /*var errRep = function(errCode, vastDoc, errMsg) {
                    pubD(0, 'ad_error', errCode, vastDoc, errMsg);
                };*/
                var cb = function(err, vast, isRepetition) {
                    // console.log('VAST', JSON.stringify(vast, null, 2) );

                    var ads = objectKeys(vast.ads);
                    // console.log('ads', ads);

                    var replaceErrorCode = function(urls, code) {
                        var I  = urls.length;
                        var urls2 = new Array(I);
                        for (var i = 0; i < I; ++i) {
                            urls2[i] = urls[i].replace(/\[ERRORCODE\]/, code);
                        }
                        return urls2;
                    };

                    var errorEndpoints = ( (ads.length > 0) ? vast.ads[ ads[0] ].error : [] ); // TODO ads[0] is wrong
                    // todo should be hardcoded to 1st ad
                    // console.log('errorEndpoints', errorEndpoints);
                    // console.log('triggeredErrors', vast.parts.triggeredErrors);

                    for (var i = 0, I = vast.parts.triggeredErrors.length, error, endpoints; i < I; ++i) {
                        error = vast.parts.triggeredErrors[i];
                        endpoints = replaceErrorCode(errorEndpoints, error.code || 900);
                        pub('vast_error', error);
                        pub('metro', endpoints, 'vast_error');
                    }

                    if (err) {
                        //console.log('VAST ERROR VIA CB: ', err);
                        return fsm.vast_wo_preroll();
                    }

                    if (Feat.isIPhoneOrIPod) { // iPhones should not display adwords nor do clickTracking
                        vast.parts.nonLinears = [];
                    }

                    if (!isRepetition) {
                        // TODO old vast format conversion
                        // .linears, .nonLinears -> .parts.*
                        // .linears is an array now, creating object for compatibility
                        var adId, o, linearsO = {}, nonLinearsO = [];
                        var item;

                        // NEW
                        if (vast.ads['*']) {
                            //console.log('FIRING WRAPPER IMPRESSIONS\n', vast.ads['*'].impression);
                            pub('metro', vast.ads['*'].impression, 'vast impression');
                            delete vast.ads['*'];
                        }
                        // NEW

                        for (i = 0, I = vast.parts.linears.length; i < I; ++i) {
                            item = vast.parts.linears[i];
                            //console.log('item:', item);
                            if (!(item.item.bag.stage in linearsO)) { // one item per stage...
                                adId = item.item.bag.adId;
                                o = {
                                    duration:     item.item.duration,
                                    tracking:     item.item.bag.tracking,
                                    videoClicks:  item.item.bag.videoClicks,
                                    adParameters: item.item.bag.adParameters,
                                    error:        vast.ads[adId].error,
                                    impression:   vast.ads[adId].impression,
                                    src:          item.item.src,
                                    mimeType:     item.mimeType,
                                    stage:        item.item.bag.stage
                                };

                                linearsO[ item.item.bag.stage ] = o;
                                linearExtras[o.src] = item;
                                // console.log(o.src, o.mimeType);
                            }
                        }

                        for (i = 0, I = vast.parts.nonLinears.length; i < I; ++i) {
                            item = vast.parts.nonLinears[i];
                            //console.log('item:', item);
                            adId = item.item.bag.adId;
                            o = {
                                mimeType:   item.mimeType,
                                impression: vast.ads[adId].impression
                            };
                            for (var k in item.item) {
                                o[k] = item.item[k];
                            }
                            nonLinearsO.push(o);
                        }

                        if (options.companionContainers.length > 0 && vast.parts.companions.length > 0) {
                            SAPO.Project.Video.VAST.handleCompanions(options.companionContainers, vast.parts.companions);
                        }

                        vast.parts.linears    = linearsO;
                        vast.parts.nonLinears = nonLinearsO;
                    }


                    // setup timeline
                    pub('vast', vast, isRepetition);
                    //console.log('NEW VAST FOR GUID', currentVideo.guidURL, vast);

                    vastDocuments[ currentVideo.guidURL ] = vast;
                    //console.log('STORED VASTDOCUMENT "' + currentVideo.guidURL + '"');

                    var preroll = vast.parts.linears.pre;
                    // console.log('PREROLL', preroll);


                    var willAutoplay = currentVideo.autoplay && va.canAutoplay();

                    setPoster( metadataDocuments[currentVideo.guidURL] );

                    if (preroll && !isRepetition) {
                        // in the preroll

                        // console.log('PREROLL\n', JSON.stringify(preroll, null, '  '));
                        
                        // this helps when video ads aren't served by SAPO. their isAd and duration fields are required for the ads workflow to work well
                        var fakeMeta = {
                            requestURL: preroll.src,
                            duration: preroll.duration,
                            isAd: true,
                            isLive: false
                        };

                        if (willAutoplay) {
                            // console.log('LLLOOOAAADDD');

                            pub('metadata_changed', fakeMeta);

                            highEnd.load({
                                requestURL: preroll.src,
                                mimeType:   preroll.mimeType,
                                autoplay:   true,
                                isAd:       true
                            });
                        }
                        else {
                            highEnd.once('h_state_changed', function(/*mt, st*/) {
                                // console.log('LLLOOOAAADDD 2');

                                pub('metadata_changed', fakeMeta);

                                highEnd.load({
                                    requestURL: preroll.src,
                                    mimeType:   preroll.mimeType,
                                    autoplay:   true,
                                    isAd:       true
                                });
                            });

                            // set old poster
                        }
                    }
                    else {
                        // in the video itself
                        if (willAutoplay) {
                            //window.top.log('CAN AUTOPLAY');
                            fsm.vast_wo_preroll_auto();


                        }
                        else {
                            //window.top.log('CANNOT AUTOPLAY');
                            fsm.vast_wo_preroll();
                        }
                    }

                };

                // request vast...

                var guid = currentVideo.guidURL;

                if (guid in vastDocuments && currentVideo.skipVastRequest) {
                    //console.log('RESTORING VAST FROM DOCS HAVING GUID: ', guid, vastDocuments[guid]);
                    cb(null, vastDocuments[guid], true);
                }
                else {
                    //SAPO.Project.Video.vast(zoneId, md, options.playerInterno, errRep, cb); // OLD VAST INTEGRATION POINT

                    var xmlUrl = SAPO.Project.Video.VAST.SapoPubVAST(zoneId, md, options.playerInterno);

                    try {
                        if (typeof window.top.OVERRIDE_VAST_URL === 'string') {
                            xmlUrl = window.top.OVERRIDE_VAST_URL;
                        }
                    } catch (ex) {}

                    // TODO TEMP
                    var getFirst = function(arr) {
                        // return undefined;

                        return arr[0];
                    };

                    var getFirstN = function(arr, n) {
                        // return [];

                        var arr2 = arr.slice();
                        var l = arr.length;
                        if (l <= n) {
                            return arr2;
                        }
                        arr2.splice(n, l - n);
                        return arr2;
                    };

                    SAPO.Project.Video.VAST.VASTRun({
                        vastXmlUrl: xmlUrl,
                        bestMediaFile: function(results) {
                            return getFirst(results);
                        },
                        bestNonLinears: function(results) {
                            return getFirstN(results, 2);
                        },
                        bestCompanions: function(results) {
                            return getFirstN(results, 2);
                        },
                        cb: cb
                    });
                }
            },

            onenterWAITING_PLAY: function() {
                if ( currentVideo.guidURL && previousTimes[currentVideo.guidURL] && !currentVideo.isAd) {
                    // console.warn('restoring time %s from video %s after playing video %s', previousTimes[currentVideo.guidURL], currentVideo.guidURL, currentVideo.previousGuidURL);
                    ui._updateTime( previousTimes[currentVideo.guidURL], true); // restores time slider before user interaction
                }

                var md = currentVideo.metadata;

                // shows poster
                setPoster(md);

                if ( va.canAutoplay() && (currentVideo.isAd || currentVideo.autoplay) ) { // coming from ad or autoplay requested (it must be possible either way)
                    return fsm.needs_playbck();
                }

                var cb = function(/*mt, st*/) {
                    if (currentVideo.zoneId === 0 || currentVideo.isAd || (va.canAutoplay() && currentVideo.autoplay) /*|| md.revisited*/) {
                        currentVideo.autoplay = true;
                        fsm.needs_playbck();
                    }
                    else {
                        currentVideo.autoplay = true;
                        fsm.needs_vast2();
                    }
                };
                
                // highEnd.once('h_state_changed', cb); // TODO POSSIBLY HACK
                highEnd.once('state_changed', cb); // TODO POSSIBLY HACK
            },

            onenterNEEDS_PLAYBCK: function() {
                var uri = currentVideo.requestURL;
                var uris = {
                    original: currentVideo.requestURL,
                    processed: currentVideo.requestURL
                };

                /*
                if (currentVideo.isAd) {
                    highEnd.once('ended', function() {
                        if (!currentVideo || !currentVideo.isAd) { // Prevent invalidate being fired on wrong video.
                            return;
                        }
                        pubD(500, 'invalidate_ad_roll', {autoplay:true, complete:true});
                    });
                }*/

                var whenVideoSrcAvailable = function(err, o) {
                    currentVideo.redirectURL = (err ? uris.original : o);
                    fsm.redirect_url_ok_error();
                };

                highEnd._inInternoMode = !!highEnd.getOption('playerInterno');
                if (highEnd._inInternoMode && !currentVideo.isLive) {
                    uris.processed = [
                        uri,
                        ( (uri.indexOf('?') === -1) ? '?' : '&' ),
                        'player=INTERNO'
                    ].join('');
                }

                // request redirect
                if (!options.noSapoVideos && currentVideo.requestURL !== currentVideo.guidURL) {
                    // Publish redirect
                    pub('video_redirect', uris);
                    SAPO.Project.Video.sapoVideosVideoSource(uris.processed, whenVideoSrcAvailable);
                } else {
                    whenVideoSrcAvailable(false, uris.original);
                }
            }
        }
    });



    // player
    var Video = function(o) {
        /*jshint maxcomplexity:30, maxstatements:120 */

        // default options
        options = SAPO.extendObj({
            // MAIN OPTIONS
             video:                      undefined
            ,autoplay:                   false
            ,startTime:                  undefined
            ,container:                  document.body
            ,zoneId:                     undefined

            // GUI-RELATED
            ,allowPlayPause:             true
            ,allowResize:                true
            ,allowScrub:                 true
            ,allowVolume:                !Feat.isMobile
            ,allowKeyboard:              true

            ,followSuggestionsInline:    false
            ,staticGUI:                  false // playerInterno = staticGUI + skipBranding = false
            ,skipShare:                  false //
            ,skipSuggestions:            false
            ,skipCssPatch:               false
            ,skipBranding:               false
            ,supportPrivates:            false
            ,noSapoVideos:               false
            ,companionContainers:        []
            ,volume: 1
            ,mute:   false

            // AUX CALLBACKS - remember to call them via VideoHighEndWrapper
            // ,onReady(inst) - called once player markup and parent iframe are ready for manipulation
        }, o || {});

        o = options;

        //console.log('options', o);



        // i18n overloading
        var i18n = SAPO.Project.Video.i18n;
        if ('i18n' in o) {
            for (var k in o.i18n) {
                if (!o.i18n.hasOwnProperty(k)) { continue; }
                i18n[k] = o.i18n[k];
            }
        }


        this._onClickBound = this._onClick.bindObj(this);



        if ('zoneId' in o) {
            try {
                if (!('hashZoneId' in o)) {
                    throw 'no hashZoneId';
                }
                if (o.hashZoneId !== SAPO.Utility.md5(o.zoneId + slt) ) {
                    throw 'wrong hashZoneId';
                }
                //console.log('zoneId passed');
            } catch (ex) {
                //console.log(ex);
                delete o.zoneId;
            }
        }

        if (typeof o.container === 'string') {
            o.container = s$(o.container);
        }



        this._staticDir = window.location.protocol + '//js.sapo.pt/Assets/Images/ProjectVideo'; // MAKE IT DEPEND ON LOCATION?!



        // setup mediator
        var med;

        if (SV_ENV === 'DEV') {
            // med = SAPO.Utility.Mediator({catchExceptions:true, debug:false}); // same as prod
            med = SAPO.Utility.Mediator({catchExceptions:false, debug:false}); // no wrapping exceptions!
            // med = SAPO.Utility.Mediator({catchExceptions:false, debug:true}); // tests
        }
        else {
            med = SAPO.Utility.Mediator({catchExceptions:true, debug:false}); // prod
        }

        /**
         * Provides a callback which will get called every time a message of the given
         * kind is generated. Supports more than one messageKind, separated by spaces.
         * The first argument on every call is the messageKind in order for the callback to
         * be able to filter by kind internally.
         *
         * @function subscribe
         * @param {String} messageKind
         * @param {Function} callbackToCall
         */
        this.subscribe = med.subscribe;

        /**
         * To unsubscribe a message kind
         *
         * @function unsubscribe
         * @param {String} messageKind
         * @param {Function} callbackToCall
         */
        this.unsubscribe = med.unsubscribe;

        /**
         * Provides a callback which will get the next time a message of the given
         * kind is generated. Supports more than one messageKind, separated by spaces.
         * The first argument on every call is the messageKind in order for the callback to
         * be able to filter by kind internally.
         *
         * @function once
         * @param {String} messageKind
         * @param {Function} callbackToCall
         */
        this.once = med.once;

        pub  = med.publish;
        pubD = med.publishDelayed;



        SAPO.Project.Video.initUtils(this, pub, pubD);



        // setup FS if Full Screen API is supported...
        FS = window.parent.SAPO.Utility.FullScreen;


        //if (true) { // use this if clause instead to test as if the browser doesn't have FullScreen API support
        if (!FS.isSupported) {
            FS = undefined;
        }
        else {
            // sends h_resize normal event in cases the user presses escape
            (function() {
                var lastNormalTime = 0;
                med.subscribe('h_resize', function(mt, a) {
                    if (a === 'normal') {
                        lastNormalTime = new Date().valueOf();
                    }
                });

                FS.change(function(isFull) {
                    if (!isFull) {
                        var dt = new Date().valueOf() - lastNormalTime;
                        if (dt > 1000) {
                            //pub('h_resize', 'normal');
                            pubD(100, 'h_resize', 'normal');
                        }
                    }
                });
            })();
        }
        //alert('FS API SUPPORT: ' + (FS ? 'Y' : 'N'));



        var el = document.createElement('div');
        el.className = 'player';
        document.body.appendChild(el);



        ui = new SAPO.Project.Video.GUI.Main(this, o, pub, pubD, el);



        highEnd = this;


        var vaInitialErrorHandler = function(mt, err) {
            pub('error', err === '' ? '' : _('flashUnavailableErrorMsg'));
        };

        va = new SAPO.Project.Video.Controllers.VideoAbstract({
            container:                 ui._els.videoCtn,
            insteadOf:                 true,
            volume:                    ('volume' in o ? o.volume : load('volume', 1)),
            mute:                      ('mute'   in o ? o.mute   : load('mute',   false)),
            prefetch:                  true,
            onReady: function(va_) {
                // console.log('ABSTRACT READY');
                va = va_;
                highEnd.whenAbstractReady(va, highEnd, ui, pub, pubD, vaInitialErrorHandler);
            }
        });

        // console.log('RIGHT AFTER ABSTRACT CTOR');

        va.on('error', vaInitialErrorHandler);


        // to give plugins ability to set options (so far, used only by quality plugin :P)
        var setOption = function(name, value) {
            options[name] = value;
        };

        // spawn plugins
        var P, plugin;
        for (var i = 0, f = plugins.length; i < f; ++i) {
            P = plugins[i];
            if(options.noSapoVideos && P._name.indexOf('sv_') === 0) { continue; }
            plugin = new P(this, ui, pub, pubD, va, setOption);
            //console.log('spawning plugin ' + plugin._name);
        }

        this.subscribe('cache_metadata', function(mt, o){
            metadataDocuments[o.guid.substring(o.guid.indexOf('//'))] = o;
        });


        this.subscribe('load', function(/*mt, dims*/) {
            lastHumanState = 'play';
            lastMachineState = 'play'; // Save the last machine state as 'play'
            highEnd._relayout(); // STOCK BROWSER STUFF
        });

        this.subscribe('invalidate_adwords', function(/*mt, o*/) {
            // console.log('invalidate_adwords running...');
            var vast = vastDocuments[ currentVideo.guidURL ];
            if (!vast) { return; } // should not happen!
            vast.parts.nonLinears = [];
            // console.log('after:', vast);
        });

        this.subscribe('state_changed', function(mt, a){
            lastMachineState = a; // Save the last machine state. Compare it on ended
            if( a === 'pause' && currentVideo.isAd && currentVideo.isReady) {
                if(lastHumanState !== a) {
                    var currentTime = highEnd.getCurrentTime();
                    var duration = highEnd.getDuration();
                    pubD(0, 'invalidate_ad_roll', {autoplay:true, complete: currentTime > (duration - 1) });
                }
            }
        });

        this.subscribe('h_state_changed', function(mt, a){
            //console.error("human state", a);
            lastHumanState = a;
        });

        this.subscribe('invalidate_ad_roll', function(mt, o) {
            lastHumanState = 'play';
            //console.log('invalidate_ad_roll');
            var vast = vastDocuments[ getPreviousGuid() ];
            if (!vast) { return/* console.log('EXITED')*/; }

            var keys = 'pre mid post'.split(' ');

            var bag, key, i;
            for (i = 0; i < 3; ++i) {
                key = keys[i];
                bag = vast.parts.linears[key];
                if (bag && bag.src === currentVideo.requestURL) {

                    if (bag.tracking && bag.tracking.complete && o.complete) { // TODO this removes SAPO cash cow (clicktracking does not complete)
                        pub('metro', bag.tracking.complete, 'vast_complete');
                    }

                    delete vast.parts.linears[key];
                    //log('invalidated ', key, ' roll');
                    //console.log('invalidated %s roll', key);
                    break;
                }
            }

            //console.log('ABOUT TO LOAD ' + getPreviousRequestURL());
            highEnd.load( getPreviousRequestURL(), !!o.autoplay, false);
        });

        this.subscribe('call', function(/*mt, dims*/) {
            var args = Array.prototype.slice.call(arguments);
            args.shift();
            var methodName = args.shift();
            var argsS = JSON.stringify(args).split('');
            argsS.shift();
            argsS.pop();
            argsS = argsS.join('');
            //console.log('CALLING videoController.%s(%s)', methodName, argsS);
            highEnd[methodName].apply(highEnd, args);
        });

        this.subscribe('error', function(mt, error) {
            // console.log('HIGH END ERROR HANDLER', error);

            if (!error && highEnd._errorEl) {
                //console.log('DELETING ERROR LAYER');
                el.removeChild(highEnd._errorEl);
                delete highEnd._errorMessageEl;
                delete highEnd._errorEl;
            }

            if (!error) { return; }

            if (!highEnd._errorEl) {
                //console.log('CREATING ERROR LAYER');
                highEnd._errorEl = document.createElement('div');
                highEnd._errorEl.className = 'fill error';
                highEnd._errorMessageEl = document.createElement('div');
                highEnd._errorMessageEl.className = 'message';
                highEnd._errorEl.appendChild(highEnd._errorMessageEl);
                el.appendChild(highEnd._errorEl);
            }

            Css.removeClassNamesStartingWith(ui._els.player, 'at-', 'body');
            Css.removeClassNamesStartingWith(ui._els.player, 'video-is-', 'not-new');

            highEnd._errorMessageEl.innerHTML = error.replace(/\\n/g, '<br/>');
        });


        // CLICK LAYER (anchor)
        this._clickEl = ui._els.aEl; // TODO: used by sv_rolls, could use overlay instead?

        Event.observe(
            document.documentElement,
            'click', this._onClickBound
        );

        // Add layout classes
        this._relayout();

        Event.observe(window, 'resize',       this._onWindowResize.bindObj(this));
        Event.observe(window, 'beforeunload', this._onBeforeUnload.bindObj(this));
    };

    Video.prototype = {

        whenAbstractReady: function(va, highEnd, ui, pub, pubD, vaInitialErrorHandler) {
            // set UI state accordingly
            if (ui._volumeSlider) {
                ui._volumeSlider.setValue(va._options.volume);
            }

            pub('mute', va._options.mute);

            var errorToI18n = {
                CORS_AND_FLASH_UNAVAILABLE:              'withoutCorsOrFlashErrorMsg',
                FLASH_SOURCE_NOT_FOUND:                  'noVideoSourceErrorMsg',
                FLASH_UNAVAILABLE:                       'flashUnavailableErrorMsg',
                HTML5_MEDIA_ERR_SRC_NOT_SUPPORTED:       'noVideoSourceErrorMsg',
                NO_SV_METADATA:                          'noSVMetadataErrorMsg',
                URL_OF_UNKNOWN_OR_UNSUPPORTED_MIME_TYPE: 'unknownErrorMsg',
                NO_STREAM:                               'noStreamErrorMsg',
                'NETCONNECTION.CONNECT.REJECTED':        'noStreamErrorMsg',
                'STREAM IS NOT PUBLIC':                  'noStreamErrorMsg'
            };

            va.on('error', function(mt, error) {
                va.off('error', vaInitialErrorHandler);

                // var i18nKey = errorToI18n[error] || 'unknownErrorMsg';
                var i18nKey = errorToI18n[error ? error.replace(/^\s+|\s+$|\n+$/g, '').toUpperCase() : error] || 'unknownErrorMsg';

                if (error) {
                    error = _(i18nKey);
                }

                pub('error', error);
            });

            va.on('ended', function(evName, ev) {
                var isFake = ev === true;
                if(!va.isPlaying() && lastMachineState === 'play'){ // Fix for IE and other browsers (?) not pausing the video when it ends.
                    pub('state_changed', 'pause');
                }

                if(isFake && !currentVideo.isAd) { // Don't trigger the ended if it's a fake 'ended' from a fullscreen on iphone and the video is an ad.
                    return;
                }

                pub('ended', {
                    requestURL: currentVideo.requestURL,
                    isAd:       currentVideo.isAd,
                    isLive:     currentVideo.isLive,
                    isFake:     isFake
                });
            });

            va.on('warning', function(msg) {
                pub('warning', msg);
            });

            va.on('impression', function() {
                var vast = vastDocuments[ currentVideo.previousGuidURL ];
                var impression = vast.parts.linears.pre.impression;
                pub('metro', impression, 'vast impression');
            });

            va.on('pause', function() {
                pub('state_changed', 'pause');
            });

            va.on('play', function() {
                pub('error', '');

                pub('state_changed', 'play');
            });

            va.on('progress', function() {
                pub('progress');
            });

            va.on('durationchange', function() {
                if (currentVideo.metadata.fromSapoVideos) { return; }

                var d = va.getDuration();
                if (!isFinite(d)) { return; }

                currentVideo.metadata.duration = d;
                currentVideo.metadata.isAd     = currentVideo.isAd;

                pub('metadata_changed', currentVideo.metadata);
            });

            va.on('loadedmetadata', function(mt, dims) {
                currentVideo.isReady = true;
                if (!currentVideo.metadata.fromSapoVideos) {
                    var d = va.getDuration();
                    var isLive = currentVideo.isLive || !isFinite(d);

                    currentVideo.metadata.requestURL = highEnd.getCurrentRequestURL();
                    currentVideo.metadata.duration   = (isLive ? undefined : d);
                    currentVideo.metadata.dimensions = dims;
                    currentVideo.metadata.isAd       = currentVideo.isAd;
                    currentVideo.metadata.isLive     = currentVideo.isLive;

                    // console.log('loadedmetadata -> metadata_changed\n' + JSON.stringify(currentVideo.metadata, null, '  '));
                    pub('metadata_changed', currentVideo.metadata);
                    // pubD(0, 'metadata_changed', currentVideo.metadata); // TODO WHY?

                    /*if (currentVideo.isAd) {
                        va.on('ended', function() {
                            //console.log('INVALIDATING!');
                            pubD(0, 'invalidate_ad_roll', {autoplay:true, complete:true});
                        });
                    }*/
                }
            });

            va.on('timeupdate', function(mt, ct) {
                pub('time_updated', ct);
            });

            va.on('volumechange', function(mt, v) {
                pub('volume_changed', v);
            });

            va.on('impl_ready', function(mt, impl){
                pub('impl_ready', impl);
            });

            va.on('waiting', function() { pub('waiting'); });
            va.on('playing', function() { pub('playing'); });

            va.on('click', function(mt, ev) { // COMES FROM FLASH W/ IE < 10
                if (parseInt(SAPO.Browser.version, 10) === 8) {
                    Event.fire(ui._els.aEl, 'click', ev);
                }
                else {
                    highEnd._onClick(ev);// console.log('SENT EVENT');
                }
            });

            /*if (!Feat.hasCORS && !Feat.hasFlash) {
                setTimeout(function() {
                    va.fire('error', 'CORS_AND_FLASH_UNAVAILABLE');
                    va.kill();
                }, 50);
            }*/

            var v = highEnd.getOption('video');
            if (v) {
                currentVideo.autoplay = highEnd.getOption('autoplay');
                this.load(v, currentVideo.autoplay);
            }
        },

        /**
         * @function getCurrentState
         * @returns {String} current player FSM state
         */
        getCurrentState: function() {
            return fsm.current;
        },

        /**
         * @function load
         * @param {String}  requestURL
         * @param {Boolean} [autoplay]
         *
         * BEHOLD! this can received object instead and will convert regular signature to object!
         */
        load: function(requestURL_, autoplay_/*, isAd*/) {

            var o;
            if (typeof requestURL_ === 'string') {
                o = {
                    requestURL: requestURL_,
                    autoplay:   !!autoplay_,
                    isAd:       !!arguments[2]
                };
            }
            else {
                o = requestURL_;
            }

            //console.log('LOAD: ' + JSON.stringify(o));

            /*if (o.requestURL === currentVideo.requestURL) { // TODO FIX THIS ASAP!!!
                //console.error('REPEAT LOAD');
                return;
            }*/


            if (o.requestURL && o.requestURL.indexOf('/') === -1) {
                o.requestURL = [ Feat.pageProtocol() + '//rd3.videos.sapo.pt', o.requestURL, 'mov/1'].join('/');
            }

            var m = processVideoSourceRgx.exec(o.requestURL);
            processVideoSourceRgx.lastIndex = 0;

            var isExternal = !m;
            var isLive = false;
            if (!isExternal && !o.mimeType) {
                if (m[5] === '24') {
                    isLive = true;
                    o.mimeType = ['rtmp/mp4', 'application/vnd.apple.mpegurl'];
                }
                else {
                    var q = o.quality || options.quality;
                    if (q) {
                        q = q.toLowerCase();
                        o.requestURL = replaceEncId(o.requestURL, (q === 'hd') ? '39' : '1');
                    }
                    o.mimeType = 'video/mp4';
                }
            }
            else if (isExternal) {
                if      ((/\.mp4$/i ).test(o.requestURL)) { o.mimeType = 'video/mp4'; }
                else if ((/\.flv$/i ).test(o.requestURL)) { o.mimeType = 'video/flv'; }
                else if ((/\.webm$/i).test(o.requestURL)) { o.mimeType = 'video/webm'; }
                else if ((/^rtmp/i  ).test(o.requestURL)) { o.mimeType = 'rtmp/mp4'; isLive = true; }
                else if ((/\.m3u8/i).test(o.requestURL)) { o.mimeType = 'application/vnd.apple.mpegurl'; /*isLive = true;*/ }
                else if ((/\.mp3$/i ).test(o.requestURL)) { o.mimeType = 'audio/mp3'; }
            }

            var wasAd = !!currentVideo.isAd;

            currentVideo.requestURL = o.requestURL;

            var guid, instanceHash;
            try {
                if (!options.noSapoVideos && !isExternal) {
                    instanceHash = o.requestURL.lastIndexOf('#');
                    instanceHash = ( (instanceHash !== -1) ? '#' + o.requestURL.substring(instanceHash+1) : '' );
                    guid = o.requestURL.split('/');
                    guid.pop(); // remove encid(#\d)
                    guid.pop(); // remove mov
                    guid = guid.join('/') + instanceHash;
                    guid = guid.substring(guid.indexOf('//'));
                    currentVideo.guidURL = guid;
                } else {
                    currentVideo.guidURL = o.requestURL;
                }
            } catch (ex) {
                currentVideo.guidURL = o.requestURL; // better than nothing I guess
            }

            var isSameGuid = false;

            if (guid !== guidHistory[guidHistory.length - 1]) {
                guidCount[guid] = guidCount[guid] || 0;
                guidCount[guid] += 1;
                      guidHistory.push( guid );
                requestURLHistory.push( o.requestURL );
            }
            else {
                isSameGuid = true;
            }

            if (guidHistory.length > 1 && !wasAd && !!o.isAd) { // Starting on the second video, save the previous time
                var previousGuid = getPreviousGuid();

                if (guidCount[previousGuid] > 1) {
                    // console.warn('saving time %s for %s before playing %s', highEnd.getCurrentTime(), previousGuid, guid);
                    previousTimes[ previousGuid ] = highEnd.getCurrentTime();
                }
            }

            // quality change OR coming back from roll
            currentVideo.skipVastRequest = isSameGuid || ( wasAd && (guidHistory.length > 2) && (guid === guidHistory[ guidHistory.length - 3]) );

            currentVideo.autoplay    = !!o.autoplay;
            currentVideo.isAd        = !!o.isAd;
            currentVideo.wasAd       = wasAd;
            currentVideo.isLive      = isLive;
            currentVideo.metadata    = {};
            currentVideo.zoneId      = undefined;
            currentVideo.redirectURL = undefined;
            currentVideo.isExternal  = isExternal;
            currentVideo.mimeType    = o.mimeType;
            currentVideo.isReady     = false;

            //console.log('CURRENT VIDEO -> ' + JSON.stringify(currentVideo, null, '  '));

            fsm.load_msg();
        },

        /**
         * retuns a player option or default if it has a default
         *
         * @function getOption
         * @param {String} optionName
         * @return the option's value
         */
        getOption: function(optionName) {
            return options[ optionName ];
        },


        /**
         * pauses the video
         *
         * @function pause
         */
        pause: function() { va.pause(); },

        /**
         * plays the video
         *
         * @function play
         */
        play: function() { va.play(); },

        /**
         * moves the playback to the specified time
         *
         * @function setCurrentTime
         * @param {Number}  t               time to go to, in seconds
         * @param {Boolean} [userInitiated] use true if action was generated by user interaction
         */
        setCurrentTime: function(t, userInitiated) {
            if (this.isAd()) {
                return;
            }
            if (userInitiated) {
                pub('h_scrub', t);
            }
            va.setCurrentTime(t);
        },

        setCurrentTime2: function(t) {
            va.setCurrentTime2(t);
        },

        /**
         * sets the player volume
         *
         * @function setVolume
         * @param {Number}  vol volume to set to, 0 for muted, 1 for maximum
         */
        setVolume: function(vol) {
            if      (vol < 0) { vol = 0; }
            else if (vol > 1) { vol = 1; }

            if(vol) {
                Css.removeClassName(ui._els.player, 'muted');
            }

            save('volume', vol);

            va.setVolume(vol);
        },

        /**
         * stop playback (pauses and returns to the beginning)
         *
         * @function stop
         */
        stop: function() {
            va.stop();
        },

        /**
         * mutes the video's volume
         *
         * @function mute
         */
        mute: function() {
            save('mute', true);

            va.mute();
            pub('mute', true);
        },

        /**
         * unmutes the sound
         *
         * @function unmute
         */
        unmute: function() {
            save('mute', false);

            va.unmute();
            pub('mute', false);
        },

        /**
         * toggle sound mute state
         *
         * @function toggleMute
         * @return {Boolean} true iif end state is muted
         */
        toggleMute: function() {
            if (this.isMuted()) {
                this.unmute();
            }
            else {
                this.mute();
            }
        },

        /**
         * toggle playback state
         *
         * @function togglePlay
         * @return {Boolean} true iif end state is playing
         */
        togglePlay: function() { // returns true if moved to play state
            va.togglePlay();
        },

        /**
         * increment volume by a delta
         *
         * @function changeVolume
         * @param {Number} dv delta volume
         */
        changeVolume: function(dv) {
            var v = this.getVolume();
            this.setVolume(v + dv);
        },

        /**
         * scrub the video, i.e., move in the timeline by dt seconds
         *
         * @function scrub
         * @param {Number} dt delta time in seconds
         */
        scrub: function(dt) {
            var t = this.getCurrentTime() + dt;
            var d = this.getDuration();
            if (t < 0) { t = 0; }
            if (t > d) { t = d; }
            this.setCurrentTime(t, true);
        },

        /**
         * Visually mark the video as being 'unloading'
         *
         * @function unloading
         */
        unloading: function() {
            Css.addClassName(ui._els.player, 'unloading');
            var cb = function(mt, d){
                if(d > 0) {
                    Css.removeClassName(ui._els.player, 'unloading');
                    va.off('timeupdate', cb);
                }
            };
            va.on('timeupdate', cb);
            highEnd.unload();
        },

        /**
         * @function unload
         */
        unload: function() {
            va.unload();
        },



        // GETTERS


        /**
         * returns current time, in seconds
         *
         * @function getCurrentTime
         * @return {Number} current time in seconds
         */
        getCurrentTime: function() {
            return va.getCurrentTime();
        },

        /**
         * returns current request URL (video request URI)
         *
         * @function getCurrentRequestURL
         * @return {String} current video request URI
         */
        getCurrentRequestURL: function() {
            return currentVideo.requestURL;
        },

        /**
         * returns current source (video URI)
         * if autoplay is inactive, this value isn't yet available
         *
         * @function getCurrentVideoSource
         * @return {String} current video source URI
         */
        getCurrentVideoSource: function() {
            return va.getCurrentSrc();
        },

        /**
         * returns current player dimensions, in pixels
         *
         * @function getPlayerDimensions
         * @return {Number[2]} player dimensions in pixels
         */
        getPlayerDimensions: function() {
            return this._dims;
        },

        /**
         * returns current video dimensions, in pixels
         *
         * @function getVideoDimensions
         * @return {Number[2]} currently scaled size in pixels
         */
        getVideoDimensions: function() {
            return this._dimsV;
        },

        /**
         * returns the original video dimensions, in pixels
         *
         * @function getVideoOriginalDimensions
         * @return {Number[2]} original video size in pixels
         */
        getVideoOriginalDimensions: function() {
            var d = va.getVideoSize();

            // tries to minimize lack of video dimensions
            // by filling it up dimensions from metadata, if available

            //console.log('from video itself:', d.join('x'));
            if (d[0] < 100 && this._metadata && this._metadata.dimensions) {
                d = this._metadata.dimensions;
                //console.log('from metadata:', d.join('x'));
            }
            return d;
        },

        /**
         * returns the video duration in seconds
         *
         * @function getDuration
         * @return {Number} video duration in seconds
         */
        getDuration: function() {
            return va.getDuration();
        },

        /**
         * returns the video volume, a number between 0 and 1
         *
         * @function getVolume
         * @return {Number} current video volume, between 0 and 1
         */
        getVolume: function() {
            return va.getVolume();
        },

        /**
         * returns the container which gets the player iframe.
         *
         * @function getParentContainer
         * @return {DOMElement}
         */
        getParentContainer: function() {
            return this._parentContainerEl;
        },

        /**
         * @function getCache
         * @return {Object} video cache params: start, end, length
         */
        getCache: function(asRatio) {
            return va.getCache(asRatio);
        },

        /**
         * returns true iif volume is muted
         *
         * @function isMuted
         * @return {Boolean} true iif video volume is muted (0)
         */
        isMuted: function() {
            return va.isMuted();
        },

        /**
         * returns true iif video is playing
         *
         * @function isPlaying
         * @return {Boolean} true iif video is playing
         */
        isPlaying: function() {
            return va.isPlaying();
        },

        /**
         * returns true iif video implementation is HTML5, otherwise Flash
         *
         * @function isHtml5
         * @return {Boolean} iif video player implementation is HTML5
         */
        isHtml5: function() {
            return va.getCurrentImplName() === 'html5';
        },

        /**
         * returns true iif player is displaying an ad roll
         *
         * @function isAd
         * @return {Boolean} iif playing an ad
         */
        isAd: function() {
            return currentVideo.isAd;
        },

        /**
         * returns true iif player is displaying a live video
         *
         * @function isLive
         * @return {Boolean} iif is live
         */
        isLive: function() {
            return currentVideo.isLive;
        },

        /**
         * returns true if video is HD
         *
         * @function isHD
         * @return {Boolean} iif is HD
         */
        isHD: function(url){
            url = url || currentVideo.requestURL;
            if (!currentVideo) {
                return false;
            }
            return (/\/39$/).test(url); // TODO should rely on metadata instead?
        },

        // DIMENSION SETTERS


        /**
         * returns true if player is maximized
         *
         * @function inFullScreen
         * @return {Boolean} iif in full screen state (maximized)
         */
        inFullScreen: function() {
            if (FS) {
                return FS.inFullscreen();
            }

            return Css.hasClassName(this._iframeEl, options.iframeMaximizedClass);
        },

        /**
         * Requests full screen mode.
         * Won't work programatically if in Full Screen API (change must be triggered by human generated event)
         *
         * @function setFullScreen
         */
        setFullScreen: function() {
            if (FS) {
                return FS.request(this._iframeEl);
            }
            else if (Feat.isIPad && inIframe) {
                var timeTimer = window.timeTimer;
                var videoEl = va.getElement();

                var onFSClick = function() {
                    if (timeTimer) { return; }

                    window.timeTimer = timeTimer = window.setInterval(function() {
                        try {
                            videoEl.webkitEnterFullscreen();
                        }
                        catch(e) {}
                    }, 250);

                    videoEl.play(); // is this required? YES
                };

                var onBeginFS = function() {
                    window.clearInterval(timeTimer);
                    timeTimer = undefined;
                    window.timeTimer = undefined;
                };

                var onEndFS = function() {
                    videoEl.pause(); // is this required? NO
                };

                videoEl.addEventListener('webkitbeginfullscreen', onBeginFS);
                videoEl.addEventListener('webkitendfullscreen',   onEndFS);

                onFSClick();

                return;
            }

            Css.addClassName(this._iframeEl, options.iframeMaximizedClass);

            var s = this._iframeEl.style;
            s.position = 'fixed';
            s.left     = 0;
            s.right    = 0;
            s.top      = 0;
            s.bottom   = 0;
            s.width    = '100%';
            s.height   = '100%';
            s.zIndex   = 99999999;
        },

        /**
         * Requests full screen state exit.
         * Won't work programatically if in Full Screen API (change must be triggered by human generated event)
         *
         * @function unsetFullScreen
         */
        unsetFullScreen: function() {
            if (FS) {
                return FS.exit(this._iframeEl);
            }
            else if (Feat.isIPad && inIframe) {
                return;
            }

            var s = this._iframeEl.style;
            var isStockBrowser = Feat.isAndroid && !Feat.isChromeForAndroid;

            Css.removeClassName(this._iframeEl, options.iframeMaximizedClass);


            if(isStockBrowser) { // Hack for "Internet" "Browser" in Android 4.1
                s.position = 'static';
                setTimeout(function(){
                    s.position = 'absolute';
                }, 0);
            } else {
                s.position = 'absolute';
            }
            s.left     = '';
            s.right    = '';
            s.top      = '';
            s.bottom   = '';
            s.zIndex   = '';
            s.width    = '';
            s.height   = '';
        },

        /**
         * Requests full screen state toggle.
         * Won't work programatically if in Full Screen API (change must be triggered by human generated event)
         *
         * @function toggleFullScreen
         */
        toggleFullScreen: function() {
            if (this.inFullScreen()) {
                this.unsetFullScreen();
            }
            else {
                this.setFullScreen();
            }

            this._relayout();
        },

        /**
         * Resizes the player iframe via style.
         * Will be ignored when in full screen.
         * Notice that you can easily affect the player iframe via CSS
         *
         * @param {Number[2]} dims  desired dimensions, in pixels
         * @param {Boolean}   force don't use this publicly please!
         */
        resize: function(dims, force) {
            if (!force) {
                if (this.inFullScreen()) { return; }
            }

            var s = this._iframeEl.style;

            if (dims && dims.length === 2) {
                s.width  = dims[0] + 'px';
                s.height = dims[1] + 'px';
            }
            else {
                s.width  = '';
                s.height = '';
            }
        },

        /**
         * Returns true iif player instance is high end (offering more features and public methods).
         *
         * @function isHighEnd
         * @return {Boolean}
         */
        isHighEnd: function() {
            return true;
        },

        /**
         * sets the video title
         *
         * @param {String} title
         */
        setTitle: function(title) {
            ui.setTitle(title);
        },

        /**
         * sets the video author
         *
         * @param {String} author
         */
        setAuthor: function(author) {
            ui.setAuthor(author);
        },

        /**
         * sets the video metadata
         *
         * @function setMetadata
         * @param {Object} md
         */
        setMetadata: function(md) {
            //console.log("setting metadata", md);
            currentVideo.metadata = md;
            setPoster(md);
            pub('metadata_changed', currentVideo.metadata);
        },

        switchImpl: function(cb) {
            if (!cb) { cb = function() {}; }

            var currImplName = va.getImplName();
            var desiredImplName = ( currImplName === 'html5' ? 'flash' : 'html5' );
            if (va.isImplAvailable(desiredImplName)) {
                //console.log('switching from impl ' + currImplName + ' to ' + desiredImplName + '...');
                //options.autoplay = true;
                va._options.autoplay = currentVideo.autoplay;
                va.switchToImpl(
                    desiredImplName,
                    function() {
                        cb(null, {
                            message: 'impl change: ' + currImplName + ' -> ' + desiredImplName,
                            from:    currImplName,
                            to:      desiredImplName
                        });
                    }
                );
            }
            else {
                cb('unsupported impl: ' + desiredImplName);
            }
        },



        // PROTECTED STUFF

        _relayout: function() {
            var dims = this._getViewportSize();

            var inFS = this.inFullScreen();

            if (this._dims &&
                dims[0] === this._dims[0] &&
                dims[1] === this._dims[1]) {
                return;
            }

            //window.top.console.log('RELAYOUT', dims.join('x'));

            var guiH = options.staticGUI ? (Feat.hasTouchEvents ? 70 : 48) : 0; // (40+30 / 40+8)

            var dimsV = [dims[0], dims[1] - guiH];

            this._dims  = dims;
            this._dimsV = dimsV;

            var staticMode = options.staticGUI && !inFS;

            var smallMode = (this._dims[0] <= 360); /*300/250*/

            //console.log('small mode?', smallMode ? 'Y' : 'N');
            Css.setClassName(ui._els.player, 'small',   smallMode);
            Css.setClassName(ui._els.player, 'big',    !smallMode);
            Css.setClassName(ui._els.player, 'static', staticMode);

            va.resize(dimsV); // for vpaid

            pub('resize', {
                container: dims,
                video:     dimsV,
                maximized: inFS
            });
        },

        _onWindowResize: function() {
            this._relayout();
        },

        _getViewportSize: function() {
            return Element.windowDimensions();
        },

        _onClick: function(ev) {
            /*jshint maxcomplexity:20 */

            //window.top.log('CONTROLLER EVENT HANDLER (window) type: ' + ev.type);

            if (!va.isReady() && (va.getCurrentImplName() === 'flash' || va.getCurrentImplName() === 'flashhls')) { // to let user click on flash blocker
                return;
            }

            /*if (va.getCurrentImplName() === 'flashhls') {
                return Event.stop(ev);
            }*/

            var tgtEl = ev && Event.element(ev);

            if (Element.findUpwardsBySelector(tgtEl, '[rel]')) {
                return this.pause();
            }

            if (
                !options.allowPlayPause ||
                Element.findUpwardsBySelector(tgtEl, '.hud-top, .hud-bottom, .dialog, .ad-words')
                ) {
                return;
            }

            ui._onTogglePlay(ev);
        },

        _onBeforeUnload: function() {
            pub('before_unload');
        }
    };



    SAPO.Project.Video.Controllers.Video = Video;

})(window);
